import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from './auth.service';
import { catalogReturnTarget } from './catalog-return-target';

type AccessEvaluator = (authService: AuthService) => boolean;

function requireAccess(evaluator: AccessEvaluator): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    return evaluator(authService)
      ? true
      : router.createUrlTree(['/login']);
  };
}

export const requireAuth: CanActivateFn = (_route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const catalog = catalogReturnTarget(state.url);
  const login = () => router.createUrlTree(['/login'], catalog ? { queryParams: { catalog_return: catalog } } : undefined);

  if (authService.authStatus !== 'authenticated') {
    return login();
  }

  if (authService.tokenInvalidOrExpired(authService.token)) {
    authService.resetSession();

    return login();
  }

  return true;
};

export const requireAdminAccess = requireAccess(
  (authService) => authService.can('manageUsers', 'user'),
);

export const requireAdminProjectsAccess = requireAccess(
  (authService) => authService.can('adminProjects', 'user'),
);

export const requireAdminAreaAccess = requireAccess(
  (authService) => authService.can('adminProjects', 'runtime')
    || authService.can('production', 'runtime')
    || authService.can('invoicing', 'runtime'),
);

export const requireCanAdvanceOrderStatus = requireAccess(
  (authService) => authService.can('advanceOrder', 'runtime'),
);

export const requireProductionViewAccess = requireAccess(
  (authService) => authService.can('production', 'runtime'),
);

export const requireAdminOrgEventsAccess = requireAccess(
  (authService) => authService.can('viewAdminEvents', 'user'),
);

export const requireCustomerProfilesAdminAccess = requireAccess(
  (authService) => authService.can('customerProfilesAdmin', 'user'),
);

export const requireBatteryCatalogAdminAccess = requireAccess(
  (authService) => authService.can('batteryCatalog', 'runtime'),
);

export const requireFullAdminOrgAccess = requireAccess(
  (authService) => authService.can('fullAdmin', 'user'),
);

export const requireResellerEventsAccess = requireAccess(
  (authService) => authService.can('viewResellerEvents', 'user'),
);

export const requireResellerWorkspaceAccess = requireAccess(
  (authService) => authService.can('resellerWorkspace', 'user'),
);

export const requireResellerAdminAccess = requireAccess(
  (authService) => authService.can('resellerAdmin', 'runtime'),
);

export const requireQuoteTemplateAccess = requireAccess(
  (authService) => authService.can('manageQuoteTemplates', 'runtime'),
);

export const requireSuperadminAccess = requireAccess(
  (authService) => authService.can('superAdmin', 'user'),
);

export const requireAccountingAccess = requireAccess(
  (authService) => authService.can('invoicing', 'user'),
);

export const requireEstimateSettingsAccess = requireAccess(
  auth => auth.can('manageEstimateSettings'),
);
